import { useFlag } from '@innovamat/flags';
import { useFluencyCourseDecksQuery } from '@innovamat/glow-api-client';
import { useMemo } from 'react';
import { useUser } from '../../user-management';
import type { CourseDecks } from '../modules/fluency/course-decks';
import {
  hasDecks as checkHasDecks,
  allDecks as getAllDecks,
  type DeckInfo,
} from '../modules/fluency/course-decks';

type Props = {
  classroomId: string;
};

type UseFluencyCourseDecks = {
  allDecks: DeckInfo[];
  courseDecks: CourseDecks[];
  hasDecks: boolean;
  isLoading: boolean;
};

function useFluencyCourseDecks({ classroomId }: Props): UseFluencyCourseDecks {
  const { value: showFluencyReports } = useFlag('showFluencyReports', false, {
    identifier: '',
    custom: {
      classroomId: classroomId || '',
    },
  });

  const { isRole } = useUser();

  const { data, isLoading } = useFluencyCourseDecksQuery(
    { classroomId: classroomId || '' },
    {
      retry: false,
      enabled: Boolean(
        showFluencyReports && classroomId && !isRole.Student && !isRole.Parent
      ),
    }
  );

  const courseDecks = data?.fluencyCourseDecks || [];

  const hasDecks = checkHasDecks(courseDecks || []);

  const allDecks = useMemo(
    () => getAllDecks(courseDecks || []),
    [data?.fluencyCourseDecks]
  );

  return { allDecks, courseDecks, hasDecks, isLoading };
}

export { useFluencyCourseDecks };
